import { Box, Tab, Tabs, Tooltip } from '@mui/material';
import { useIntl } from 'react-intl';
import OptimizationObjectivesComponent from '~/containers/viewer/table-panel/optimization-objectives';
import { CurveData } from '~/domain/patient/dvh';
import DoseStatisticsPane from './DoseStatisticsPane';
import { DoseTable } from './useDoseTableSelection';

interface DoseTablesPaneProps {
    /** The curves the statistics table reads. */
    readonly curves: CurveData[];
    /** The table shown. */
    readonly table: DoseTable;
    readonly onSelectTable: (table: DoseTable) => void;
    /**
     * Whether the dose has objectives. Without them the objectives tab is
     * disabled and says why on hover.
     */
    readonly objectivesAvailable: boolean;
}

const TAB_STRIP_HEIGHT_PX = 28;

/**
 * The tables of one dose behind a tab strip, filling the box it is given:
 * the dose statistics and the optimization objectives, one shown at a
 * time. The selection is the host's, so it outlives this pane.
 */
export default function DoseTablesPane({
    curves,
    table,
    onSelectTable,
    objectivesAvailable,
}: DoseTablesPaneProps) {
    const intl = useIntl();
    return (
        <Box
            sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <Tabs
                value={table}
                onChange={(_, value) => onSelectTable(value as DoseTable)}
                aria-label={intl.formatMessage({ id: 'dose.panel.tables' })}
                indicatorColor="primary"
                textColor="inherit"
                sx={{
                    flexShrink: 0,
                    minHeight: TAB_STRIP_HEIGHT_PX,
                    bgcolor: 'background.paper',
                    borderBottom: 1,
                    borderColor: 'divider',
                    '& .MuiTab-root': {
                        minHeight: TAB_STRIP_HEIGHT_PX,
                        px: 1.5,
                        py: 0.5,
                        fontSize: 12,
                        textTransform: 'none',
                    },
                }}
            >
                <Tab
                    value="statistics"
                    label={intl.formatMessage({ id: 'dose.panel.statistics' })}
                    data-testid="dose-table-tab-statistics"
                />
                <Tab
                    value="objectives"
                    disabled={!objectivesAvailable}
                    data-testid="dose-table-tab-objectives"
                    label={
                        // describeChild: the reason describes the tab, it
                        // does not rename it. It opens up, away from the
                        // sortable column headers of the table under the
                        // strip.
                        <Tooltip
                            describeChild
                            placement="top"
                            title={
                                objectivesAvailable
                                    ? ''
                                    : intl.formatMessage({
                                          id: 'optimizationObjectivesNotAvailable',
                                      })
                            }
                        >
                            {/* A disabled tab takes no pointer events; the
                                label takes them back so the reason shows. */}
                            <Box
                                component="span"
                                sx={{ pointerEvents: 'auto' }}
                            >
                                {intl.formatMessage({
                                    id: 'dose.panel.objectives',
                                })}
                            </Box>
                        </Tooltip>
                    }
                />
            </Tabs>
            <Box sx={{ position: 'relative', flex: 1, minHeight: 0 }}>
                {table === 'objectives' ? (
                    <OptimizationObjectivesComponent />
                ) : (
                    <DoseStatisticsPane curves={curves} />
                )}
            </Box>
        </Box>
    );
}
